import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';

import { MatStepper } from '@angular/material/stepper';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { SubProductsService } from '../../../Services/sub-products.service';
import { SubComponentsService } from '../../../Services/sub-components.service';
import { LoadingService } from '../../../Services/Loading/loading-service.service';
import { BackendAccessService } from '../../../Services/APICalls/backend-access-service';
import {
  ProductResponse,
  ZentraComponent,
} from '../../../interfaces/subProduct.interface';
import { SatAdminSuccessDialogComponent } from '../sat-admin-success-dialog/sat-admin-success-dialog.component';
import {
  customPriceValidator,
  integerValidator,
  strictEmailValidator,
} from '../../_shared-admin/validators/admin-validators';
import { buildComponentLineItems } from '../../_shared-admin/utils/subscription-component-payload.util';

@Component({
  selector: 'app-sat-admin-stepper',
  templateUrl: './sat-admin-stepper.component.html',
  styleUrls: ['./sat-admin-stepper.component.scss'],

  standalone: false,
})
export class SatAdminStepperComponent {
  @ViewChild('stepper') stepper!: MatStepper;
  isLinear = true;
  firstFormGroup!: FormGroup;
  secondFormGroup!: FormGroup;
  products: ProductResponse[] = [];
  selectedProduct: ProductResponse | null = null;
  componentOptions: ZentraComponent[] = [];
  selectedComponents: { component: ZentraComponent; quantity: number }[] = [];
  totalPrice = 0;
  isEditing = false;
  originalFormValues: any = null;
  isDataReady = false;
  purchaserEmailErrorMessage = '';

  constructor(
    readonly formBuilder: FormBuilder,
    readonly productService: SubProductsService,
    readonly subComponentsService: SubComponentsService,
    readonly loadingService: LoadingService,
    readonly cdr: ChangeDetectorRef,
    readonly backend: BackendAccessService,
    readonly dialog: MatDialog,
    readonly route: ActivatedRoute
  ) {}

  /* istanbul ignore next */
  ngOnInit() {
    this.initForms();
    this.loadProducts();

    this.components.valueChanges.subscribe(() => {
      this.updateSelectedComponents();
    });
  }

  /* istanbul ignore next */
  loadProducts() {
    this.loadingService.show();
    this.productService.getSATProducts().subscribe({
      next: (products) => { this.products = products; this.loadingService.hide(); },
      error: () => this.loadingService.hide(),
    });
    this.subComponentsService.fetchSATComponentOptions();
    this.subComponentsService.getComponentOptions().subscribe({
      next: (components) => this.componentOptions = components,
    });
  }

  /* istanbul ignore next */
  selectProduct(product: ProductResponse) {
    this.selectedProduct = product;
    this.firstFormGroup.patchValue({ productId: product.product.id.toString() });
  }

  /* istanbul ignore next */
  initForms() {
    this.firstFormGroup = this.formBuilder.group({
      subscriptionIdentifier: ['', Validators.required],
      productId: ['', Validators.required],
    });

    this.secondFormGroup = this.formBuilder.group({
      components: this.formBuilder.array([this.createComponentControl()]),
      metafields: this.formBuilder.group({
        distributionNetworkLevel: [''],
        customerId: [''],
      }),
      purchaserEmail: ['', [Validators.required, strictEmailValidator]],
    });
  }

  /* istanbul ignore next */
  shouldShowNextButton(): boolean {
    const idx = this.stepper?.selectedIndex ?? 0;
    const total = this.stepper?.steps?.length ?? 2;
    return idx < total - 1;
  }

  /* istanbul ignore next */
  shouldShowSaveButton(): boolean {
    const idx = this.stepper?.selectedIndex ?? 0;
    const total = this.stepper?.steps?.length ?? 2;
    return idx === total - 1;
  }

  /* istanbul ignore next */
  isCurrentStepValid(): boolean {
    const idx = this.stepper?.selectedIndex ?? 0;
    const formGroups = [this.firstFormGroup, this.secondFormGroup];
    return formGroups[idx]?.valid ?? false;
  }

  /* istanbul ignore next */
  goToNextStep() {
    const formGroups = [this.firstFormGroup, this.secondFormGroup];
    if (formGroups[this.stepper.selectedIndex]?.valid) this.stepper.next();
  }

  /* istanbul ignore next */
  goToPreviousStep() {
    if (this.stepper) this.stepper.previous();
  }

  /* istanbul ignore next */
  displayComponentName(component: ZentraComponent): string {
    return component?.name ?? '';
  }

  /* istanbul ignore next */
  onComponentChange(component: ZentraComponent | null, index: number) {
    if (!component) return;
    this.components.at(index).patchValue({ component });
    this.updateSelectedComponents();
  }

  /* istanbul ignore next */
  isComponentSelected(option: ZentraComponent, currentIndex: number): boolean {
    return this.components.controls.some((c, i) => i !== currentIndex && c.get('component')?.value?.id === option.id);
  }

  /* istanbul ignore next */
  toggleCustomPrice(index: number) {
    const control = this.components.at(index).get('customPrice');
    this.components.at(index).get('useCustomPrice')?.value ? control?.enable() : control?.disable();
  }

  /* istanbul ignore next */
  updateSelectedComponents() {
    this.selectedComponents = this.components.controls
      .map((c) => { const comp = c.get('component')?.value; const qty = c.get('allocatedQuantity')?.value ?? 1; return comp ? { component: comp, quantity: qty } : null; })
      .filter(Boolean) as { component: ZentraComponent; quantity: number }[];
    this.calculateTotalPrice();
  }

  /* istanbul ignore next */
  calculateTotalPrice() {
    this.totalPrice = this.selectedComponents.reduce((sum, item, index) => {
      const componentControl = this.components.at(index);
      const useCustomPrice = componentControl.get('useCustomPrice')?.value;
      const customPrice = parseFloat(componentControl.get('customPrice')?.value ?? '0');
      const unitPrice = useCustomPrice
        ? customPrice
        : parseFloat(item.component.prices?.[0]?.unitPrice ?? '0');
      return sum + unitPrice * item.quantity;
    }, 0);
  }

  /* istanbul ignore next */
  get metafieldsGroup(): FormGroup {
    return this.secondFormGroup.get('metafields') as FormGroup;
  }

  /* istanbul ignore next */
  createComponentControl(): FormGroup {
    return this.formBuilder.group({
      component: ['', Validators.required],
      allocatedQuantity: [
        1,
        [Validators.required, Validators.min(1), integerValidator()],
      ],
      useCustomPrice: [false],
      customPrice: [
        { value: '', disabled: true },
        [customPriceValidator(), Validators.min(0.01)],
      ],
    });
  }

  /* istanbul ignore next */
  get components(): FormArray {
    return this.secondFormGroup.get('components') as FormArray;
  }

  /* istanbul ignore next */
  addComponent() {
    this.components.push(this.createComponentControl());
  }

  /* istanbul ignore next */
  removeComponent(index: number) {
    this.components.removeAt(index);
  }

  /* istanbul ignore next */
  onSubmit() {
    if (this.firstFormGroup.invalid || this.secondFormGroup.invalid) return;
    this.loadingService.show();

    const firstFormData = this.firstFormGroup.value;
    const secondFormData = this.secondFormGroup.value;
    const metafieldsData = this.metafieldsGroup.value;

    const payload = {
      maxioProductId: firstFormData.productId,
      purchaserEmail: secondFormData.purchaserEmail,
      subscriptionIdentifier: firstFormData.subscriptionIdentifier,
      subscriptionData: {
        product_name: this.selectedProduct?.product?.name,
        product_id: firstFormData.productId,
        total_pre_tax_price: this.totalPrice.toString(),
        components: buildComponentLineItems(
          this.selectedComponents,
          this.components
        ),
        metafields: {
          distributionNetworkLevel:
            metafieldsData.distributionNetworkLevel ?? undefined,
          customerId: metafieldsData.customerId ?? undefined,
        },
      },
    };

    this.backend.submitConfiguredSubData(payload).subscribe({
      next: () => {
        this.loadingService.hide();
        this.dialog.open(SatAdminSuccessDialogComponent);
      },
      error: (error) => {
        console.error('Error submitting subscription:', error);
        this.loadingService.hide();
      },
    });
  }
}
